export type SystemUpdateKind = 'customer_paid';

export type SystemUpdate = {
  id: string;
  kind: SystemUpdateKind;
  title: string;
  body: string;
  repairId?: string;
  callNumber?: string;
  amount?: number;
  at: string;
  read: boolean;
};

const LEGACY_STORAGE_KEY = 'repairLab.systemUpdates.v1';
const STORAGE_KEY_PREFIX = 'repairLab.systemUpdates.v2:';
const MAX_ITEMS = 80;
const CHANGE_EVENT = 'repairlab:system-updates-changed';

let activeUserScope = '';

function storageKeyForScope(userScope: string): string {
  const scope = String(userScope || '').trim();
  return scope ? `${STORAGE_KEY_PREFIX}${scope}` : LEGACY_STORAGE_KEY;
}

/** קובע scope למשתמש מחובר — עדכוני תשלום נשמרים בנפרד לכל משתמש */
export function setSystemUpdatesUserScope(userId: string | null | undefined): void {
  const next = String(userId || '').trim();
  if (activeUserScope === next) return;
  activeUserScope = next;
  // מיגרציה חד־פעמית ממפתח ישן משותף → מפתח לפי משתמש
  if (typeof window !== 'undefined' && next) {
    try {
      const scopedKey = storageKeyForScope(next);
      if (!window.localStorage.getItem(scopedKey)) {
        const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        if (legacy) {
          window.localStorage.setItem(scopedKey, legacy);
        }
      }
    } catch {
      /* ignore */
    }
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function currentStorageKey(): string {
  return storageKeyForScope(activeUserScope);
}

function readAll(): SystemUpdate[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(currentStorageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object')
      .map((item) => item as SystemUpdate)
      .filter((item) => String(item.id || '').trim() && String(item.title || '').trim());
  } catch {
    return [];
  }
}

function writeAll(items: SystemUpdate[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(currentStorageKey(), JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* quota / private mode */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function listSystemUpdates(): SystemUpdate[] {
  return readAll().sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function countUnreadSystemUpdates(): number {
  return readAll().filter((item) => !item.read).length;
}

export function addSystemUpdate(
  input: Omit<SystemUpdate, 'id' | 'read' | 'at'> & { id?: string; at?: string },
): SystemUpdate {
  const id = String(
    input.id || `${input.kind}:${input.repairId || input.callNumber || ''}:${input.at || Date.now()}`,
  ).trim();
  const existing = readAll();
  const found = existing.find((item) => item.id === id);
  if (found) {
    return found;
  }
  const next: SystemUpdate = {
    id,
    kind: input.kind,
    title: input.title,
    body: input.body,
    repairId: input.repairId,
    callNumber: input.callNumber,
    amount: input.amount,
    at: input.at || new Date().toISOString(),
    read: false,
  };
  writeAll([next, ...existing]);
  return next;
}

export function markSystemUpdateRead(id: string): void {
  const items = readAll();
  let changed = false;
  const next = items.map((item) => {
    if (item.id !== id || item.read) return item;
    changed = true;
    return { ...item, read: true };
  });
  if (changed) writeAll(next);
}

export function markAllSystemUpdatesRead(): void {
  const items = readAll();
  if (!items.some((item) => !item.read)) return;
  writeAll(items.map((item) => ({ ...item, read: true })));
}

export function subscribeSystemUpdates(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onChange = () => listener();
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}
